import {
  AlertTriangle, ArrowDown, ArrowUp, Braces, ChevronDown, ChevronRight, ChevronUp, Flag, MessageSquareText, Phone,
  Plus, Repeat, RotateCcw, Settings2, SlidersHorizontal, Trash2, UserPlus,
} from "lucide-react";
import * as React from "react";
import { Button } from "@/components/ui/button";
import { ComboboxList } from "@/components/ui/combobox";
import { Switch } from "@/components/ui/misc";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Textarea } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { phaseColor } from "@/features/cadence-preview";
import {
  CountPicker, DayPicker, OptionPicker, PriorityPicker, Segmented, TextPicker, Token, WaitPicker,
  type Option, type Rung,
} from "@/features/cadence-tokens";
import {
  CADENCE_BOUNDS, MAX_REDIALS, TEXT_MAX, afterOptions, afterWords, callTags, callsFor, capProblem, hasCalls,
  hasTexts, isText, leavesMessage, repeats, stepName, stepsWords, strategiesFor, textPreview, textSnippet, textsOnly,
  waitsFor, withCall, withCallOptions, withRedials, withStepMoved, withText, withoutCall,
  type CadenceFormValues, type CallTag, type CallValues, type PhaseValues, type RedialValues,
} from "@/lib/cadence";
import {
  DAY_PRESETS, WAIT_PRESETS, cadenceSummary, phaseDays, phaseEndBounds, phaseFromDay, phaseNameProblem, waitWords,
  withPhase, withoutPhase,
} from "@/lib/cadence-plan";
import { endOutcomeLabel, endOutcomeOptions, endOutcomesLeftOut } from "@/lib/cadence-end";
import { useCadenceTextFields, useDispositions, useVoicemailMessages } from "@/lib/queries";
import type { GreetingField, OnVoicemail, VoicemailMessage } from "@/lib/types";
import { cn } from "@/lib/utils";
import { insertField } from "@/lib/voicemail";

/**
 * A cadence as a flow a lead goes down (ADR-066), drawn so the plan reads at a glance and every
 * option is one click away (ADR-103). "In short" says the whole cadence in a few lines. A lead
 * joins, each phase's calls come in rounds that start again until the phase's last day, the next
 * phase takes over, and the cadence stops. A call is one line: where it calls from, and a tag for
 * each thing that differs from the defaults. A click opens everything about that call in place: its
 * number, what it does when a voicemail answers, its redials, and whether it repeats. A phase's own
 * settings sit behind its gear, a later phase folds to one line, and the rules every phase shares
 * fold away at the bottom.
 *
 * A step can be a text instead of a call (ADR-135 in platform). It is added where a call is added,
 * is one line with the start of its words, and opens in place like a call: the words, with a
 * menu that puts a field in at the caret and what a lead would read under them, Spanish words for
 * a Spanish lead, and whether it repeats. A step is moved earlier or later from its own foot.
 */

type Set = (next: CadenceFormValues | ((v: CadenceFormValues) => CadenceFormValues), key?: string) => void;

const STRATEGIES: readonly Option<string>[] = [
  { value: "branded", label: "Branded", description: "The campaign's own caller ID, the number leads know." },
  { value: "same_as_last", label: "Same as last", description: "The number the lead was last called from, so a callback reaches the same place." },
  {
    value: "unbranded",
    label: "Local",
    description: "Another number routed to this campaign, in the lead's state when there is one (ADR-069).",
  },
  {
    value: "rotate",
    label: "Rotating",
    description: "The campaign's numbers in turn, the one used longest ago first, never the number the lead just heard (ADR-075).",
  },
];

const STRATEGY_NAMES: Record<string, string> = {
  branded: "Branded number",
  same_as_last: "Same number as the last call",
  unbranded: "Local number",
  rotate: "Rotating number",
};

/** Who leaves the message is the choice (ADR-109 in platform): a rep, who gets the voicemail on
 *  their phone, or the dialer by itself, a fixed wait after it hears the voicemail. */
const VOICEMAIL: readonly { value: OnVoicemail; label: string }[] = [
  { value: "hang_up", label: "Hang up" },
  { value: "leave_message", label: "Rep leaves a voicemail" },
  { value: "leave_message_auto", label: "Dialer leaves a voicemail" },
];

/** What each choice does, under it. */
function voicemailHelp(choice: OnVoicemail, seconds: number): string {
  if (choice === "hang_up") {
    return "Counts as no answer, so the redials go out. When the voicemail reaches the rep, their No answer, Busy or Voicemail left sends the next redial.";
  }
  if (choice === "leave_message") {
    return "The voicemail goes to the rep, who leaves the message. No redial follows it.";
  }
  const when = seconds > 0 ? `${seconds} second${seconds === 1 ? "" : "s"} after` : "as soon as";
  return `The dialer plays the message ${when} it hears the voicemail, with no rep on the call, and the rep gets their next lead. No redial follows it.`;
}

/** A Select item cannot have an empty value: this stands for "the message for the lead's stage". */
const BY_STAGE = "__stage__";

const COUNTS: readonly Option<"yes" | "no">[] = [
  { value: "yes", label: "Counts as an attempt" },
  { value: "no", label: "Does not count" },
];

export function CadenceBuilder({
  values, set, readOnly, problems, tenantId = null,
}: {
  values: CadenceFormValues;
  set: Set;
  readOnly: boolean;
  problems: Record<string, string>;
  /** The cadence's tenant: whose outcomes it may set when it runs out (ADR-155). */
  tenantId?: number | null;
}) {
  const patch = (i: number, p: Partial<PhaseValues>, key?: string) =>
    set((v) => ({ ...v, phases: v.phases.map((x, j) => (j === i ? { ...x, ...p } : x)) }), key);
  const replace = (i: number, next: PhaseValues, key?: string) =>
    set((v) => ({ ...v, phases: v.phases.map((x, j) => (j === i ? next : x)) }), key);
  const first = values.phases[0];
  const cap = values.daily_calls_per_number.trim();
  const capWarning = capProblem(values);
  // One call is open at a time; a later phase starts folded, and the gear opens one phase's settings.
  const [openCall, setOpenCall] = React.useState<string | null>(null);
  const [folded, setFolded] = React.useState<ReadonlySet<number>>(
    () => new Set(values.phases.map((_, i) => i).filter((i) => i > 0)),
  );
  const [settings, setSettings] = React.useState<number | null>(null);
  const voicemail = useVoicemailMessages();
  const messages = React.useMemo(() => (voicemail.data ?? []).filter((m) => m.active), [voicemail.data]);
  const texting = hasTexts(values);
  const dialing = hasCalls(values);
  // An admin's list, asked for once the cadence has a text to write.
  const textFields = useCadenceTextFields(texting && !readOnly).data;
  const textCap = values.daily_texts_per_lead.trim();
  const firstIsText = Boolean(first && isText(callsFor(first.attempts, first.calls)[0]!));
  const expires = values.expire_days.trim();
  const outcomes = useDispositions(tenantId).data;
  const endOptions = React.useMemo(
    () => endOutcomeOptions(outcomes ?? [], values.end_outcome),
    [outcomes, values.end_outcome],
  );
  const lastDay = (
    <Token
      label="Last day"
      readOnly={readOnly}
      invalid={problems.expire_days}
      title="The last day"
      hint="Counted from when the lead joined."
      content={(close) => (
        <DayPicker
          value={values.expire_days}
          presets={DAY_PRESETS}
          min={CADENCE_BOUNDS.expire_days[0]}
          max={CADENCE_BOUNDS.expire_days[1]}
          openLabel="No end day"
          onChange={(d) => set((v) => ({ ...v, expire_days: d }))}
          close={close}
        />
      )}
    >
      {expires ? `day ${expires}` : "no end day"}
    </Token>
  );

  return (
    // The whole width the page gives it (ACL-628): all of it while "What a lead gets" is closed.
    <div className="w-full">
      {problems.phases && <p className="mb-2 text-xs text-danger-11">{problems.phases}</p>}

      <Summary
        values={values}
        endLabel={values.end_outcome === "" ? undefined : endOutcomeLabel(values.end_outcome, outcomes)}
      />

      <Card icon={UserPlus} tone="start" title="A lead joins the campaign" tour="cadence-start">
        {first && (
          <p>
            The first {firstIsText ? "text" : "call"} goes out{" "}
            <Token
              label={firstIsText ? "First text" : "First call"}
              readOnly={readOnly}
              invalid={problems["phases.0.initial_delay_minutes"]}
              title={firstIsText ? "When the first text goes out" : "When the first call goes out"}
              hint="After the lead joins a list of a campaign that runs this cadence."
              content={(close) => (
                <WaitPicker
                  value={first.initial_delay_minutes}
                  presets={WAIT_PRESETS.firstCall}
                  min={CADENCE_BOUNDS.initial_delay_minutes[0]}
                  max={CADENCE_BOUNDS.initial_delay_minutes[1]}
                  onChange={(m) => patch(0, { initial_delay_minutes: m })}
                  close={close}
                />
              )}
            >
              {waitWords(first.initial_delay_minutes)}
            </Token>
            {first.initial_delay_minutes > 0 ? " after they join." : "."}
          </p>
        )}
      </Card>

      {values.phases.map((phase, i) => {
        const troubled = Object.keys(problems).some((k) => k.startsWith(`phases.${i}.`));
        return (
          <React.Fragment key={i}>
            <Connector label={i === 0 ? undefined : `from day ${phaseFromDay(values, i)}`} />
            <PhaseCard
              index={i}
              values={values}
              phase={phase}
              readOnly={readOnly}
              problems={problems}
              folded={folded.has(i) && !troubled}
              onFold={() =>
                setFolded((f) => {
                  const next = new Set(f);
                  if (next.has(i)) next.delete(i);
                  else next.add(i);
                  return next;
                })
              }
              settingsOpen={settings === i}
              onSettings={() => setSettings((s) => (s === i ? null : i))}
              openCall={openCall?.startsWith(`${i}:`) ? Number(openCall.split(":")[1]) : null}
              onOpenCall={(k) => setOpenCall(k === null ? null : `${i}:${k}`)}
              messages={messages}
              textFields={textFields}
              patch={(p, key) => patch(i, p, key)}
              replace={(next, key) => replace(i, next, key)}
              remove={() => {
                setSettings(null);
                setOpenCall(null);
                setFolded(new Set());
                set((v) => withoutPhase(v, i));
              }}
            />
          </React.Fragment>
        );
      })}

      <Connector>
        {!readOnly && values.phases.length < CADENCE_BOUNDS.phases[1] && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="rounded-full"
            onClick={() => {
              // The new phase is the last one, and it opens unfolded.
              setFolded((f) => {
                const next = new Set(f);
                next.delete(values.phases.length);
                return next;
              });
              set((v) => withPhase(v));
            }}
          >
            <Plus />
            Add a phase
          </Button>
        )}
      </Connector>

      <Card icon={Flag} tone="end" title="The cadence stops" tour="cadence-stop">
        {!dialing && (
          // A cadence with no call in it has no calls to count: its days end it.
          <p>
            {expires ? "On " : "With "}
            {lastDay}
            {expires ? ", counted from when the lead joined." : "."}
          </p>
        )}
        {dialing && (
        <p>
          After{" "}
          <Token
            label="Calls in all"
            readOnly={readOnly}
            invalid={problems.total_attempts}
            title="Calls in all"
            hint="Calls that count: our own failures do not. Every redial counts."
            content={(close) => (
              <CountPicker
                value={values.total_attempts}
                min={CADENCE_BOUNDS.total_attempts[0]}
                max={CADENCE_BOUNDS.total_attempts[1]}
                presets={[10, 20, 30, 40, 60, 80, 100]}
                unit="calls"
                onChange={(n) => set((v) => ({ ...v, total_attempts: n ?? v.total_attempts }), "total")}
                close={close}
              />
            )}
          >
            {`${values.total_attempts} calls`}
          </Token>{" "}
          in all,{" "}
          {expires ? "or on " : "with "}
          {lastDay}
          {expires ? ", whichever comes first." : "."}
        </p>
        )}
        {dialing && (
        <p>
          {cap ? "Never more than " : "No limit on "}
          <Token
            label="Calls to one number in a day"
            readOnly={readOnly}
            invalid={problems.daily_calls_per_number}
            title="Calls to one number in a day"
            hint="On the lead's clock, counting every campaign, a rep's hand dial and every redial."
            content={(close) => (
              <CountPicker
                value={cap ? Number(cap) : null}
                min={CADENCE_BOUNDS.daily_calls_per_number[0]}
                max={CADENCE_BOUNDS.daily_calls_per_number[1]}
                presets={[1, 2, 3, 4, 5, 6, 8, 10]}
                noneLabel="No cap"
                unit="a day"
                onChange={(n) => set((v) => ({ ...v, daily_calls_per_number: n === null ? "" : String(n) }), "cap")}
                close={close}
              />
            )}
          >
            {cap ? `${cap} call${cap === "1" ? "" : "s"}` : "calls"}
          </Token>{" "}
          to one number in a day{cap ? ", from any campaign." : "."}
        </p>
        )}
        {texting && (
          <p>
            Never more than{" "}
            <Token
              label="Texts to one number in a day"
              readOnly={readOnly}
              invalid={problems.daily_texts_per_lead}
              title="Texts to one number in a day"
              hint="On the lead's clock, counting every cadence's texts. A text a rep sends is not counted. A text past the cap is passed over, and what follows it is still made."
              content={(close) => (
                <CountPicker
                  value={textCap ? Number(textCap) : null}
                  min={CADENCE_BOUNDS.daily_texts_per_lead[0]}
                  max={CADENCE_BOUNDS.daily_texts_per_lead[1]}
                  presets={[1, 2, 3, 4, 5]}
                  unit="a day"
                  onChange={(n) => n !== null && set((v) => ({ ...v, daily_texts_per_lead: String(n) }), "text-cap")}
                  close={close}
                />
              )}
            >
              {textCap ? `${textCap} text${textCap === "1" ? "" : "s"}` : "texts"}
            </Token>{" "}
            to one number in a day, from any cadence.
          </p>
        )}
        <p>
          When it runs out,{" "}
          <Token
            label="Outcome when it runs out"
            // With no tenant there is no catalog to pick from, nor one core-api checks against.
            readOnly={readOnly || tenantId === null}
            invalid={problems.end_outcome}
            title="When it runs out"
            hint="Its calls used up, its last day passed or its last phase over, with nobody reached. The outcome then does what it does when a person sets it."
            width="w-96"
            scrollsItself
            content={(close) => (
              // Forty-odd outcomes: searched and scrolled, grouped by where each sends the lead.
              <ComboboxList
                label="Outcomes"
                options={endOptions}
                value={values.end_outcome === "" ? null : values.end_outcome}
                clearLabel="Set nothing: the lead stays in their stage"
                plainHints
                footer={endOutcomesLeftOut(outcomes ?? []) > 0 ? "Do-not-call outcomes are not here: a cadence never marks a lead do-not-call." : undefined}
                onPick={(slug) => {
                  set((v) => ({ ...v, end_outcome: slug ?? "" }), "end-outcome");
                  close();
                }}
                className="-mx-3 -mb-3 flex-1 border-t border-border"
                // The panel is a column and the list its only part that grows and shrinks, so on a
                // short window it is the list that scrolls, whatever the heading and footer take.
                listClassName="max-h-72 min-h-24 flex-1"
              />
            )}
          >
            {values.end_outcome === "" ? "set nothing" : `set ${endOutcomeLabel(values.end_outcome, outcomes)}`}
          </Token>
          {values.end_outcome === "" ? ": the lead stays in their stage, on no list." : "."}
        </p>
        {tenantId === null && !readOnly && (
          <p className="text-xs text-text-muted">This cadence has no tenant, so it cannot name an outcome to set.</p>
        )}
        {capWarning && (
          <p className="flex items-start gap-2 rounded-lg border border-warning-4 bg-warning-2 px-2.5 py-1.5 text-xs leading-5 text-warning-11">
            <AlertTriangle className="mt-0.5 size-3.5 shrink-0" aria-hidden />
            {capWarning}
          </p>
        )}
      </Card>

      <Rules values={values} set={set} readOnly={readOnly} problems={problems} />
    </div>
  );
}

// --- the whole cadence in a few lines ----------------------------------------------------------------

function Summary({ values, endLabel }: { values: CadenceFormValues; endLabel?: string }) {
  const { lines, limits } = cadenceSummary(values, endLabel);
  return (
    <section
      aria-label="In short"
      data-tour="cadence-summary"
      className="mb-4 rounded-xl border border-border bg-surface px-4 py-3 shadow-xs"
    >
      <p className="text-[11px] font-semibold uppercase tracking-wide text-text-subtle">In short</p>
      <dl className="mt-1.5 space-y-1 text-sm">
        {lines.map((line, i) => (
          <div key={`${line.label}-${i}`} className="grid gap-x-3 @lg:grid-cols-[7rem_minmax(0,1fr)]">
            <dt className="font-medium text-text">{line.label}</dt>
            <dd className="text-text-muted">{line.text}</dd>
          </div>
        ))}
      </dl>
      <p className="mt-2 border-t border-dashed border-border pt-2 text-xs text-text-muted">{limits}</p>
    </section>
  );
}

// --- a phase ----------------------------------------------------------------------------------------

function PhaseCard({
  index, values, phase, readOnly, problems, folded, onFold, settingsOpen, onSettings, openCall, onOpenCall, messages,
  textFields, patch, replace, remove,
}: {
  index: number;
  values: CadenceFormValues;
  phase: PhaseValues;
  readOnly: boolean;
  problems: Record<string, string>;
  folded: boolean;
  onFold: () => void;
  settingsOpen: boolean;
  onSettings: () => void;
  openCall: number | null;
  onOpenCall: (call: number | null) => void;
  messages: VoicemailMessage[];
  /** The fields a text can use; undefined until they are read. */
  textFields: readonly GreetingField[] | undefined;
  patch: (p: Partial<PhaseValues>, key?: string) => void;
  replace: (next: PhaseValues, key?: string) => void;
  remove: () => void;
}) {
  const at = (f: string) => problems[`phases.${index}.${f}`];
  const calls = Number.isFinite(phase.attempts) ? Math.max(1, Math.min(phase.attempts, CADENCE_BOUNDS.attempts[1])) : 1;
  const waits = waitsFor(calls, phase.between_minutes);
  const strategies = strategiesFor(calls, phase.did_strategy);
  const options = callsFor(calls, phase.calls);
  const again = repeats(phase);
  const last = index === values.phases.length - 1;
  const callIssues = [at("attempts"), at("between_minutes"), at("did_strategy"), at("calls")].filter(Boolean);
  const { lines } = cadenceSummary(values);
  const mixed = options.some(isText);
  const onlyTexts = textsOnly(phase);
  const move = (k: number, by: -1 | 1) => {
    replace(withStepMoved(phase, k, by));
    onOpenCall(k + by);
  };

  return (
    <article
      aria-label={`Phase ${index + 1}, ${phaseDays(values, index)}`}
      // The page's tour points at the first phase.
      data-tour={index === 0 ? "cadence-phase" : undefined}
      className={cn(
        "relative overflow-hidden rounded-xl border bg-surface shadow-xs",
        Object.keys(problems).some((k) => k.startsWith(`phases.${index}.`)) ? "border-danger-9" : "border-border",
      )}
    >
      <span className={cn("absolute inset-y-0 left-0 w-1", phaseColor(index))} aria-hidden />
      <header
        className={cn(
          "flex flex-wrap items-center gap-x-2 gap-y-1 py-2 pl-3 pr-2",
          (!folded || settingsOpen) && "border-b border-border",
        )}
      >
        <button
          type="button"
          onClick={onFold}
          aria-expanded={!folded}
          aria-label={folded ? `Open ${phaseDays(values, index)}` : `Fold ${phaseDays(values, index)}`}
          className="grid size-6 shrink-0 place-items-center rounded-md text-text-subtle hover:bg-surface-hover hover:text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-8/40"
        >
          <ChevronRight className={cn("size-4 transition-transform", !folded && "rotate-90")} aria-hidden />
        </button>
        <h3 className="text-sm font-semibold text-text">{phaseDays(values, index)}</h3>
        <span className="font-mono text-xs text-text-muted">{phase.name}</span>
        {folded && <span className="min-w-0 flex-1 truncate text-xs text-text-muted">· {lines[index]?.text}</span>}
        <span className="ml-auto flex items-center gap-1 text-xs tabular-nums text-text-muted">
          priority {phase.priority_band}
          {phase.age_decay_per_day > 0 && <span>−{phase.age_decay_per_day}/day</span>}
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label={`Settings of ${phaseDays(values, index)}`}
            aria-pressed={settingsOpen}
            className={cn(settingsOpen && "bg-surface-hover text-text")}
            onClick={onSettings}
          >
            <SlidersHorizontal />
          </Button>
        </span>
      </header>

      {settingsOpen && (
        <PhaseSettings
          index={index}
          values={values}
          phase={phase}
          calls={options.filter((o) => !isText(o)).length}
          readOnly={readOnly}
          problems={problems}
          patch={patch}
          remove={remove}
        />
      )}

      {!folded && (
        <ol className="space-y-0 py-3 pl-4 pr-3 text-sm text-text-muted">
          {Array.from({ length: calls }, (_, k) => (
            <React.Fragment key={k}>
              {k > 0 && (
                <li className="relative flex items-center gap-2 py-1 pl-9">
                  <Rail />
                  <span>
                    {waits[k - 1] === 0 ? "then " : "then, "}
                    <Token
                      label={`Wait before ${stepName(options[k]!, k).toLowerCase()}`}
                      readOnly={readOnly}
                      invalid={at("between_minutes")}
                      title={`Before ${stepName(options[k]!, k).toLowerCase()}`}
                      hint={
                        isText(options[k - 1]!)
                          ? "From when the text before it goes out."
                          : "From the end of the call before it, its redials included. Right away goes out on the dialer's next pick."
                      }
                      content={(close) => (
                        <WaitPicker
                          value={waits[k - 1] ?? 1}
                          presets={WAIT_PRESETS.between}
                          min={CADENCE_BOUNDS.between_minutes[0]}
                          max={CADENCE_BOUNDS.between_minutes[1]}
                          onChange={(m) => patch({ between_minutes: waits.map((x, j) => (j === k - 1 ? m : x)) })}
                          close={close}
                        />
                      )}
                    >
                      {waitWords(waits[k - 1] ?? 1)}
                    </Token>
                    {waits[k - 1] === 0 ? "" : " later"}
                  </span>
                </li>
              )}
              <li className="relative">
                {openCall === k && isText(options[k]!) ? (
                  <TextEditor
                    index={k}
                    phase={phase}
                    steps={calls}
                    call={options[k]!}
                    readOnly={readOnly}
                    fields={textFields}
                    problem={at(`calls.${k}.text`)}
                    replace={replace}
                    onRemove={() => {
                      onOpenCall(null);
                      replace(withoutCall(phase, k));
                    }}
                    onMove={(by) => move(k, by)}
                    onClose={() => onOpenCall(null)}
                  />
                ) : openCall === k ? (
                  <CallEditor
                    index={k}
                    phase={phase}
                    calls={calls}
                    strategy={strategies[k] ?? "branded"}
                    call={options[k]!}
                    readOnly={readOnly}
                    messages={messages}
                    onStrategy={(s) => patch({ did_strategy: strategies.map((x, j) => (j === k ? s : x)) })}
                    replace={replace}
                    onRemove={() => {
                      onOpenCall(null);
                      replace(withoutCall(phase, k));
                    }}
                    onMove={(by) => move(k, by)}
                    onClose={() => onOpenCall(null)}
                  />
                ) : isText(options[k]!) ? (
                  <TextRow
                    index={k}
                    call={options[k]!}
                    problem={at(`calls.${k}.text`)}
                    tour={index === 0 && k === 0}
                    onOpen={() => onOpenCall(k)}
                  />
                ) : (
                  <CallRow
                    index={k}
                    strategy={strategies[k] ?? "branded"}
                    call={options[k]!}
                    tour={index === 0 && k === 0}
                    onOpen={() => onOpenCall(k)}
                  />
                )}
              </li>
            </React.Fragment>
          ))}
          {!readOnly && calls < CADENCE_BOUNDS.attempts[1] && (
            <li className="relative flex flex-wrap items-center gap-x-2 gap-y-1 py-1 pl-9">
              <Rail />
              <button
                type="button"
                onClick={() => {
                  replace(withCall(phase));
                  onOpenCall(calls);
                }}
                className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-medium text-accent-11 hover:bg-accent-2"
              >
                <Plus className="size-3.5" />
                Add a call
              </button>
              <button
                type="button"
                data-tour={index === 0 ? "cadence-add-text" : undefined}
                onClick={() => {
                  replace(withText(phase));
                  onOpenCall(calls);
                }}
                className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-medium text-accent-11 hover:bg-accent-2"
              >
                <MessageSquareText className="size-3.5" />
                Add a text
              </button>
            </li>
          )}
          {callIssues.length > 0 && <li className="py-1 pl-9 text-xs text-danger-11">{callIssues[0]}</li>}
          {again.kind === "once" ? (
            <li className="flex items-start gap-2 pt-2">
              <span className="grid size-6 shrink-0 place-items-center rounded-full bg-gray-3 text-text-muted" aria-hidden>
                <Flag className="size-3" />
              </span>
              <span className="min-w-0 pl-1 leading-6">
                Every {mixed ? "step" : "call"} here is made once, so this phase runs one round.{" "}
                {last ? "Then the cadence stops." : `Then the lead waits for day ${phaseFromDay(values, index + 1)}.`}
              </span>
            </li>
          ) : (
            <li className="flex items-start gap-2 pt-2">
              <span className="grid size-6 shrink-0 place-items-center rounded-full bg-gray-3 text-text-muted" aria-hidden>
                <Repeat className="size-3" />
              </span>
              <span className="min-w-0 pl-1 leading-6">
                Again{" "}
                <Token
                  label="Wait before the next round"
                  readOnly={readOnly}
                  invalid={at("after_cycle_minutes")}
                  title={onlyTexts ? "Before the texts start again" : mixed ? "Before the round starts again" : "Before the calls start again"}
                  hint={
                    onlyTexts
                      ? "From when the round's last text goes out. A round of only texts starts again after an hour at least."
                      : calls > 1 ? "From the end of the round's last step." : "From the end of the call."
                  }
                  content={(close) => (
                    <WaitPicker
                      value={phase.after_cycle_minutes}
                      presets={WAIT_PRESETS.cycle}
                      min={onlyTexts ? CADENCE_BOUNDS.text_cycle_minutes[0] : CADENCE_BOUNDS.wait_minutes[0]}
                      max={CADENCE_BOUNDS.wait_minutes[1]}
                      onChange={(m) => patch({ after_cycle_minutes: m })}
                      close={close}
                    />
                  )}
                >
                  {waitWords(phase.after_cycle_minutes)}
                </Token>{" "}
                {calls > 1 ? `after the last ${isText(options[calls - 1]!) ? "text" : "call"}` : "later"}
                {again.kind === "some" && (
                  <>
                    , with <span className="font-medium text-text">{stepsWords(options, again.calls)}</span>
                  </>
                )}
                , until <EndToken index={index} values={values} phase={phase} readOnly={readOnly} problem={at("ends_days")} patch={patch} />.
              </span>
            </li>
          )}
        </ol>
      )}
    </article>
  );
}

/** The day a phase ends, as a word in its sentence. */
function EndToken({
  index, values, phase, readOnly, problem, patch,
}: {
  index: number;
  values: CadenceFormValues;
  phase: PhaseValues;
  readOnly: boolean;
  problem: string | undefined;
  patch: (p: Partial<PhaseValues>) => void;
}) {
  const bounds = phaseEndBounds(values, index);
  const open = phase.ends_days.trim() === "";
  return (
    <Token
      label="Phase ends"
      readOnly={readOnly}
      invalid={problem}
      title="The phase ends on"
      hint={bounds.open ? "Counted from when the lead joined. The last phase may run until the cadence stops." : "Counted from when the lead joined."}
      content={(close) => (
        <DayPicker
          value={phase.ends_days}
          presets={DAY_PRESETS}
          min={bounds.min}
          max={bounds.max}
          openLabel={bounds.open ? "Until the cadence stops" : undefined}
          onChange={(d) => patch({ ends_days: d })}
          close={close}
        />
      )}
    >
      {open ? "the cadence stops" : `day ${phase.ends_days.trim()}`}
    </Token>
  );
}

/** What the gear opens: the phase's name, its last day, where it ranks, what a late call does. */
function PhaseSettings({
  index, values, phase, calls, readOnly, problems, patch, remove,
}: {
  index: number;
  values: CadenceFormValues;
  phase: PhaseValues;
  /** The round's calls, its texts left out: only a call after another call can be too late. */
  calls: number;
  readOnly: boolean;
  problems: Record<string, string>;
  patch: (p: Partial<PhaseValues>, key?: string) => void;
  remove: () => void;
}) {
  const at = (f: string) => problems[`phases.${index}.${f}`];
  const others: Rung[] = [
    { label: "A promised callback", value: values.priority_callback },
    { label: "A lead near signing", value: values.priority_about_to_sign },
    { label: "Second call of a double dial", value: values.priority_mid_cycle },
    { label: "A lead never reached", value: values.priority_initial_fresh },
    ...values.phases.filter((_, j) => j !== index).map((p) => ({ label: `Phase “${p.name}”`, value: p.priority_band })),
  ];
  return (
    <div
      role="group"
      aria-label={`Settings of ${phaseDays(values, index)}`}
      className="grid gap-x-6 gap-y-2 border-b border-border bg-surface-sunken px-4 py-3 text-sm leading-7 text-text-muted @lg:grid-cols-2"
    >
      <label className="flex items-start gap-2 @lg:col-span-2">
        <Switch
          className="mt-1.5"
          checked={Boolean(phase.prefer_window)}
          disabled={readOnly}
          onCheckedChange={(on) => patch({ prefer_window: on }, "prefer-window")}
          aria-label="Start rounds in the lead's calling window"
        />
        <span>
          <span className="text-text">Start rounds in the lead&rsquo;s calling window.</span>{" "}
          A round that comes due outside it waits for it to open; its later calls and redials, and a promised callback, go as
          usual. A lead with no window is called as before.
        </span>
      </label>
      <p>
        Named{" "}
        <Token
          label="Phase name"
          text={phase.name}
          tone="muted"
          readOnly={readOnly}
          invalid={at("name")}
          title="Phase name"
          hint="It names the phase in a lead's history and the priority reasons."
          content={(close) => (
            <TextPicker
              label="Phase name"
              mono
              value={phase.name}
              validate={(n) => phaseNameProblem(values, index, n)}
              onChange={(name) => patch({ name })}
              close={close}
            />
          )}
        >
          <span className="font-mono text-xs">{phase.name}</span>
        </Token>
        , runs until <EndToken index={index} values={values} phase={phase} readOnly={readOnly} problem={at("ends_days")} patch={patch} />.
      </p>
      <p>
        Who is called first:{" "}
        <Token
          label="Priority"
          text={`${phase.priority_band}${phase.age_decay_per_day > 0 ? `, dropping ${phase.age_decay_per_day} a day` : ""}`}
          readOnly={readOnly}
          invalid={at("priority_band") ?? at("age_decay_per_day")}
          title="Who is called first"
          hint="When there are more leads due than reps free."
          content={() => (
            <PriorityPicker
              band={phase.priority_band}
              decay={phase.age_decay_per_day}
              others={others}
              onChange={(band, decay) => patch({ priority_band: band, age_decay_per_day: decay }, `priority-${index}`)}
            />
          )}
        >
          <span className="tabular-nums">
            <span className="font-normal opacity-80">priority </span>
            {phase.priority_band}
            {phase.age_decay_per_day > 0 && <span className="font-normal opacity-80"> −{phase.age_decay_per_day}/day</span>}
          </span>
        </Token>
      </p>
      {calls > 1 && (
        <p>
          {phase.not_after_minutes.trim() ? "A call held back " : "A call held back goes out "}
          <Token
            label="Late second call"
            readOnly={readOnly}
            invalid={at("not_after_minutes")}
            title="A call running late"
            hint="The calling hours or the daily cap can hold a call that is not the round's first. Past this, the next round starts instead."
            content={(close) => (
              <>
                <OptionPicker
                  value={phase.not_after_minutes.trim() ? "wait" : "never"}
                  options={[{ value: "never", label: "Never drop it", description: "It goes out however late." }]}
                  onChange={() => patch({ not_after_minutes: "" })}
                  close={close}
                />
                <div className="mt-2 border-t border-border pt-2">
                  <p className="mb-1.5 text-[11px] font-medium uppercase tracking-wide text-text-subtle">Drop it after</p>
                  <WaitPicker
                    value={phase.not_after_minutes.trim() ? Number(phase.not_after_minutes) : 120}
                    presets={WAIT_PRESETS.lateCall}
                    min={CADENCE_BOUNDS.wait_minutes[0]}
                    max={CADENCE_BOUNDS.wait_minutes[1]}
                    onChange={(m) => patch({ not_after_minutes: String(m) })}
                    close={close}
                  />
                </div>
              </>
            )}
          >
            {phase.not_after_minutes.trim() ? waitWords(Number(phase.not_after_minutes)) : "however late"}
          </Token>
          {phase.not_after_minutes.trim() ? " late is dropped, and the next round starts." : "."}
        </p>
      )}
      {!readOnly && values.phases.length > 1 && (
        <p className="@lg:col-span-2">
          <button
            type="button"
            onClick={remove}
            className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-medium text-danger-11 hover:bg-danger-2"
          >
            <Trash2 className="size-3.5" aria-hidden />
            Remove this phase
          </button>
        </p>
      )}
    </div>
  );
}

// --- a call -----------------------------------------------------------------------------------------

const TAG_STYLE: Record<CallTag["kind"], string> = {
  redials: "bg-accent-3 text-accent-11",
  voicemail: "bg-warning-2 text-warning-11",
  once: "border border-dashed border-border-strong bg-surface-sunken text-text",
  spanish: "bg-success-2 text-success-11",
};

/** A call as one line: its place, where it calls from, and what differs from the defaults. */
function CallRow({
  index, strategy, call, tour, onOpen,
}: {
  index: number;
  strategy: string;
  call: CallValues;
  /** The first call of the first phase is the tour's. */
  tour: boolean;
  onOpen: () => void;
}) {
  const tags = callTags(call);
  return (
    <button
      type="button"
      onClick={onOpen}
      data-tour={tour ? "cadence-call" : undefined}
      aria-label={`Call ${index + 1}, ${STRATEGY_NAMES[strategy] ?? strategy}${tags.length ? `, ${tags.map((t) => t.text).join(", ")}` : ""}. Open its options`}
      className={cn(
        "group flex w-full items-center gap-2.5 rounded-lg border border-border bg-surface px-2 py-1.5 text-left transition-colors",
        "hover:border-border-strong hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-8/40",
      )}
    >
      <span
        className={cn(
          "grid size-6 shrink-0 place-items-center rounded-full",
          call.only_once ? "border border-dashed border-border-strong bg-surface text-text-muted" : "bg-accent-3 text-accent-11",
        )}
        aria-hidden
      >
        <Phone className="size-3" />
      </span>
      <span className="min-w-0 flex-1 leading-tight">
        <span className="block text-sm font-medium text-text">Call {index + 1}</span>
        <span className="block truncate text-xs text-text-muted">{STRATEGY_NAMES[strategy] ?? strategy}</span>
      </span>
      {tags.length > 0 && (
        <span className="flex flex-wrap justify-end gap-1">
          {tags.map((t) => (
            <span
              key={t.kind}
              className={cn("inline-flex items-center gap-1 rounded-full px-2 text-[11px] font-medium leading-5", TAG_STYLE[t.kind])}
            >
              {t.kind === "redials" && <RotateCcw className="size-2.5" aria-hidden />}
              {t.text}
            </span>
          ))}
        </span>
      )}
      <ChevronRight className="size-4 shrink-0 text-text-subtle" aria-hidden />
    </button>
  );
}

/** A call, open: every option it has, each a short row of choices with a line saying what it does. */
function CallEditor({
  index, phase, calls, strategy, call, readOnly, messages, onStrategy, replace, onRemove, onMove, onClose,
}: {
  index: number;
  phase: PhaseValues;
  calls: number;
  strategy: string;
  call: CallValues;
  readOnly: boolean;
  messages: VoicemailMessage[];
  onStrategy: (strategy: string) => void;
  replace: (next: PhaseValues, key?: string) => void;
  onRemove: () => void;
  onMove: (by: -1 | 1) => void;
  onClose: () => void;
}) {
  const name = `Call ${index + 1}`;
  const set = (options: Partial<CallValues>) => replace(withCallOptions(phase, index, options));
  const setRedial = (r: number, change: Partial<RedialValues>) =>
    set({ redials: call.redials.map((x, j) => (j === r ? { ...x, ...change } : x)) });
  const described = STRATEGIES.find((s) => s.value === strategy)?.description;

  return (
    <div role="group" aria-label={`${name} options`} className="@container overflow-hidden rounded-xl border border-accent-8 bg-surface shadow-md">
      <div className="flex items-center gap-2.5 border-b border-border bg-accent-2 px-2 py-1.5">
        <span className="grid size-6 shrink-0 place-items-center rounded-full bg-accent-9 text-white dark:text-gray-1" aria-hidden>
          <Phone className="size-3" />
        </span>
        <span className="text-sm font-semibold text-text">{name}</span>
        <Button type="button" variant="ghost" size="sm" className="ml-auto" onClick={onClose}>
          <ChevronUp />
          Close
        </Button>
      </div>

      <div className="divide-y divide-border">
        <Section label="Calls from">
          <Segmented
            label={`${name} calls from`}
            value={strategy}
            options={STRATEGIES}
            onChange={onStrategy}
            disabled={readOnly}
          />
          {described && <Help>{described}</Help>}
        </Section>

        <Section label="If a voicemail answers">
          <div className="flex flex-wrap items-center gap-2">
            <Segmented
              label={`${name}: if a voicemail answers`}
              value={call.on_voicemail}
              options={VOICEMAIL}
              onChange={(v) => set({ on_voicemail: v })}
              disabled={readOnly}
            />
            {leavesMessage(call.on_voicemail) && (
              <MessageSelect
                label={`${name}: the voicemail to leave`}
                value={call.voicemail_message}
                messages={messages}
                disabled={readOnly}
                onChange={(m) => set({ voicemail_message: m })}
              />
            )}
            {call.on_voicemail === "leave_message_auto" && (
              <AfterSelect
                label={`${name}: when the dialer plays the voicemail`}
                value={call.voicemail_after_seconds}
                disabled={readOnly}
                onChange={(n) => set({ voicemail_after_seconds: n })}
              />
            )}
          </div>
          <Help>{voicemailHelp(call.on_voicemail, call.voicemail_after_seconds)}</Help>
          {call.on_voicemail === "leave_message_auto" && <TimedForNow />}
        </Section>

        <Section label="If nobody answers">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-text">Redial right away, same rep</span>
            <Segmented
              label={`${name}: redials`}
              value={String(call.redials.length)}
              options={Array.from({ length: MAX_REDIALS + 1 }, (_, n) => ({ value: String(n), label: String(n) }))}
              onChange={(n) => replace(withRedials(phase, index, Number(n)))}
              disabled={readOnly}
            />
          </div>
          {call.redials.length > 0 && (
            <div className="overflow-hidden rounded-lg border border-border">
              <div className="hidden grid-cols-[5.5rem_minmax(0,1fr)_minmax(0,1.4fr)] gap-2 border-b border-border bg-surface-sunken px-2 py-1 text-[11px] text-text-subtle @md:grid">
                <span>Redial</span>
                <span>Calls from</span>
                <span>If a voicemail answers</span>
              </div>
              <div className="divide-y divide-border">
              {call.redials.map((r, j) => (
                <div
                  key={j}
                  className="grid items-center gap-2 px-2 py-1.5 @md:grid-cols-[5.5rem_minmax(0,1fr)_minmax(0,1.4fr)]"
                >
                  <span className="inline-flex items-center gap-1.5 text-xs font-medium text-text">
                    <RotateCcw className="size-3 text-accent-11" aria-hidden />
                    Redial {j + 1}
                  </span>
                  <Select value={r.did_strategy} onValueChange={(s) => setRedial(j, { did_strategy: s })} disabled={readOnly}>
                    <SelectTrigger aria-label={`Redial ${j + 1} calls from`} className="h-7 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STRATEGIES.map((s) => (
                        <SelectItem key={s.value} value={s.value} className="text-xs">
                          {s.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <span className="flex min-w-0 flex-wrap gap-1.5">
                    <Select
                      value={r.on_voicemail}
                      onValueChange={(v) => setRedial(j, { on_voicemail: v as OnVoicemail })}
                      disabled={readOnly}
                    >
                      <SelectTrigger aria-label={`Redial ${j + 1}: if a voicemail answers`} className="h-7 w-auto min-w-[10.5rem] flex-1 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {VOICEMAIL.map((v) => (
                          <SelectItem key={v.value} value={v.value} className="text-xs">
                            {v.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {leavesMessage(r.on_voicemail) && (
                      <MessageSelect
                        label={`Redial ${j + 1}: the voicemail to leave`}
                        value={r.voicemail_message}
                        messages={messages}
                        disabled={readOnly}
                        onChange={(m) => setRedial(j, { voicemail_message: m })}
                      />
                    )}
                    {r.on_voicemail === "leave_message_auto" && (
                      <AfterSelect
                        label={`Redial ${j + 1}: when the dialer plays the voicemail`}
                        value={r.voicemail_after_seconds}
                        disabled={readOnly}
                        onChange={(n) => setRedial(j, { voicemail_after_seconds: n })}
                      />
                    )}
                  </span>
                </div>
              ))}
              </div>
            </div>
          )}
          {/* Said once: under the call's own choice when that is the dialer's, else here. */}
          {call.on_voicemail !== "leave_message_auto" &&
            call.redials.some((r) => r.on_voicemail === "leave_message_auto") && <TimedForNow />}
          <Help>
            {call.redials.length
              ? "The rep stays on this lead until the last redial: no other lead and no inbound call in between. Each redial counts as a call."
              : "Made at once for the same rep, before the cadence carries on. Each redial counts as a call."}
          </Help>
        </Section>

        <Section label="Repeats">
          <Segmented
            label={`${name} repeats`}
            value={call.only_once ? "once" : "every"}
            options={[
              { value: "every", label: "Every round" },
              { value: "once", label: "Only once" },
            ]}
            onChange={(v) => set({ only_once: v === "once" })}
            disabled={readOnly}
          />
          <Help>
            {call.only_once
              ? "Made in this phase's first round only. When the calls start again, it is skipped together with its redials and the wait after it."
              : "Made in every round of this phase."}
          </Help>
        </Section>
      </div>

      {!readOnly && (
        <StepFoot what="call" index={index} steps={calls} onRemove={onRemove} onMove={onMove} onClose={onClose} />
      )}
    </div>
  );
}

/** The foot of an open step: take it out, move it earlier or later in its round, and close it. */
function StepFoot({
  what, index, steps, onRemove, onMove, onClose,
}: {
  what: "call" | "text";
  index: number;
  steps: number;
  onRemove: () => void;
  onMove: (by: -1 | 1) => void;
  onClose: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2 border-t border-border bg-surface-sunken px-2 py-1.5">
      {steps > 1 && (
        <>
          <Button type="button" variant="ghost" size="sm" className="text-danger-11" onClick={onRemove}>
            <Trash2 />
            Remove this {what}
          </Button>
          <Button type="button" variant="ghost" size="sm" disabled={index === 0} onClick={() => onMove(-1)}>
            <ArrowUp />
            Earlier
          </Button>
          <Button type="button" variant="ghost" size="sm" disabled={index >= steps - 1} onClick={() => onMove(1)}>
            <ArrowDown />
            Later
          </Button>
        </>
      )}
      <Button type="button" variant="default" size="sm" className="ml-auto" onClick={onClose}>
        Done
      </Button>
    </div>
  );
}

// --- a text (ADR-135 in platform) ---------------------------------------------------------------------

/** A text as one line: its place, the start of its words, and what differs from the defaults. */
function TextRow({
  index, call, problem, tour, onOpen,
}: {
  index: number;
  call: CallValues;
  /** Why its words would be refused, shown where the words are. */
  problem: string | undefined;
  tour: boolean;
  onOpen: () => void;
}) {
  const tags = callTags(call);
  const words = textSnippet(call.text);
  return (
    <button
      type="button"
      onClick={onOpen}
      data-tour={tour ? "cadence-call" : undefined}
      aria-label={`Text ${index + 1}${words ? `, ${words}` : ""}${tags.length ? `, ${tags.map((t) => t.text).join(", ")}` : ""}. Open its options`}
      className={cn(
        "group flex w-full items-center gap-2.5 rounded-lg border bg-surface px-2 py-1.5 text-left transition-colors",
        "hover:border-border-strong hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-8/40",
        problem ? "border-danger-9" : "border-border",
      )}
    >
      <span
        className={cn(
          "grid size-6 shrink-0 place-items-center rounded-full",
          call.only_once ? "border border-dashed border-border-strong bg-surface text-text-muted" : "bg-success-2 text-success-11",
        )}
        aria-hidden
      >
        <MessageSquareText className="size-3" />
      </span>
      <span className="min-w-0 flex-1 leading-tight">
        <span className="block text-sm font-medium text-text">Text {index + 1}</span>
        <span className={cn("block truncate text-xs", problem ? "text-danger-11" : "text-text-muted")}>{problem ?? words}</span>
      </span>
      {tags.length > 0 && (
        <span className="flex flex-wrap justify-end gap-1">
          {tags.map((t) => (
            <span
              key={t.kind}
              className={cn("inline-flex items-center gap-1 rounded-full px-2 text-[11px] font-medium leading-5", TAG_STYLE[t.kind])}
            >
              {t.text}
            </span>
          ))}
        </span>
      )}
      <ChevronRight className="size-4 shrink-0 text-text-subtle" aria-hidden />
    </button>
  );
}

const SPANISH_PREFIX = "Spanish: ";

/** A text, open: its words with the fields a lead's record fills, the same in Spanish when the
 *  admin wants it, and whether it repeats. */
function TextEditor({
  index, phase, steps, call, readOnly, fields, problem, replace, onRemove, onMove, onClose,
}: {
  index: number;
  phase: PhaseValues;
  steps: number;
  call: CallValues;
  readOnly: boolean;
  fields: readonly GreetingField[] | undefined;
  problem: string | undefined;
  replace: (next: PhaseValues, key?: string) => void;
  onRemove: () => void;
  onMove: (by: -1 | 1) => void;
  onClose: () => void;
}) {
  const name = `Text ${index + 1}`;
  const set = (options: Partial<CallValues>, key?: string) => replace(withCallOptions(phase, index, options), key);
  const [spanish, setSpanish] = React.useState(call.text_es.trim() !== "");
  const ownProblem = problem && !problem.startsWith(SPANISH_PREFIX) ? problem : undefined;
  const spanishProblem = problem?.startsWith(SPANISH_PREFIX) ? problem.slice(SPANISH_PREFIX.length) : undefined;
  const preview = textPreview(call.text, fields);

  return (
    <div role="group" aria-label={`${name} options`} className="@container overflow-hidden rounded-xl border border-accent-8 bg-surface shadow-md">
      <div className="flex items-center gap-2.5 border-b border-border bg-accent-2 px-2 py-1.5">
        <span className="grid size-6 shrink-0 place-items-center rounded-full bg-success-9 text-white dark:text-gray-1" aria-hidden>
          <MessageSquareText className="size-3" />
        </span>
        <span className="text-sm font-semibold text-text">{name}</span>
        <Button type="button" variant="ghost" size="sm" className="ml-auto" onClick={onClose}>
          <ChevronUp />
          Close
        </Button>
      </div>

      <div className="divide-y divide-border">
        <Section label="The text">
          <TextWords
            label={`${name}: the words`}
            value={call.text}
            placeholder="Hi {{lead.first_name|there}}, we tried to call you about your case. Call or text us back at {{campaign.callback_number}}."
            fields={fields}
            readOnly={readOnly}
            problem={ownProblem}
            // A run of typing is one step back, not one a letter.
            onChange={(text) => set({ text }, `text-${index}`)}
          />
          {preview && (
            <div className="w-full">
              <p className="mb-1 text-[11px] text-text-subtle">What a lead reads, with example values</p>
              <p className="w-fit max-w-full whitespace-pre-line rounded-2xl rounded-bl-sm border border-success-4 bg-success-2 px-3 py-2 text-sm leading-snug text-text">
                {preview}
              </p>
            </div>
          )}
          <Help>
            A field is filled for each lead when the text goes out. Give one fallback words after a bar, as in{" "}
            <code className="font-mono text-[11px]">{"{{lead.first_name|there}}"}</code>: a text whose field has no value
            and no fallback words is not sent. The first text a number gets from us ends with how to stop them.
          </Help>
          <Help>
            A custom field of your own is in Insert field once it is ticked on the Custom fields page, under the
            fields a voicemail greeting can say.
          </Help>
        </Section>

        <Section label="In Spanish">
          <Segmented
            label={`${name}: Spanish words`}
            value={spanish ? "yes" : "no"}
            options={[
              { value: "no", label: "Same words for everyone" },
              { value: "yes", label: "Spanish words too" },
            ]}
            onChange={(v) => {
              setSpanish(v === "yes");
              if (v === "no" && call.text_es !== "") set({ text_es: "" });
            }}
            disabled={readOnly}
          />
          {spanish && (
            <TextWords
              label={`${name}: the Spanish words`}
              value={call.text_es}
              placeholder="Hola {{lead.first_name|}}, le llamamos por su caso. Llámenos o escríbanos al {{campaign.callback_number}}."
              fields={fields}
              readOnly={readOnly}
              problem={spanishProblem}
              onChange={(text_es) => set({ text_es }, `text-es-${index}`)}
            />
          )}
          <Help>
            {spanish
              ? "A lead whose language is Spanish gets these words. Everyone else gets the ones above."
              : "Every lead gets the words above, whatever their language."}
          </Help>
        </Section>

        <Section label="Repeats">
          <Segmented
            label={`${name} repeats`}
            value={call.only_once ? "once" : "every"}
            options={[
              { value: "every", label: "Every round" },
              { value: "once", label: "Only once" },
            ]}
            onChange={(v) => set({ only_once: v === "once" })}
            disabled={readOnly}
          />
          <Help>
            {call.only_once
              ? "Sent in this phase's first round only. When the round starts again, it is skipped together with the wait after it."
              : "Sent in every round of this phase."}
          </Help>
        </Section>

        <Section label="When it goes">
          <Help>
            When this step is due, inside the campaign&apos;s calling hours on the lead&apos;s clock. No rep has to be
            signed in, and a text spends no call. It leaves from a number the lead already has a conversation on, else
            one of the campaign&apos;s.
          </Help>
          <Help>
            A lead who may not be texted gets none, and the cadence moves on: a STOP, do not call, or no number of
            the campaign&apos;s that can send texts.
          </Help>
        </Section>
      </div>

      {!readOnly && (
        <StepFoot what="text" index={index} steps={steps} onRemove={onRemove} onMove={onMove} onClose={onClose} />
      )}
    </div>
  );
}

/**
 * A text's words: where they are typed, how long they are, and the menu that puts a field in.
 * It remembers where the caret was when the menu was opened, since a menu takes the focus: the
 * field goes where the admin was typing, and the typing goes on after it.
 */
function TextWords({
  label, value, placeholder, fields, readOnly, problem, onChange,
}: {
  label: string;
  value: string;
  placeholder: string;
  fields: readonly GreetingField[] | undefined;
  readOnly: boolean;
  problem: string | undefined;
  onChange: (next: string) => void;
}) {
  const text = React.useRef<HTMLTextAreaElement | null>(null);
  const caret = React.useRef<[number, number] | null>(null);
  // A box nobody has been in yet reports its caret at the start. A field put in before the admin
  // has typed anything goes at the end of the words, where the next word would.
  const visited = React.useRef(false);
  const keepCaret = () => {
    const el = text.current;
    if (el && visited.current) caret.current = [el.selectionStart, el.selectionEnd];
  };
  const putField = (field: GreetingField) => {
    const [start, end] = caret.current ?? [value.length, value.length];
    const next = insertField(value, field.name, start, end);
    onChange(next.script);
    caret.current = [next.caret, next.caret];
  };
  const backToText = () => {
    const el = text.current;
    if (!el || !caret.current) return;
    el.focus();
    el.setSelectionRange(caret.current[0], caret.current[1]);
  };
  return (
    <div className="w-full space-y-1">
      <Textarea
        ref={text}
        aria-label={label}
        rows={4}
        spellCheck
        value={value}
        readOnly={readOnly}
        invalid={Boolean(problem)}
        placeholder={placeholder}
        className="min-h-24 font-sans text-sm leading-relaxed"
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => {
          visited.current = true;
        }}
        onBlur={keepCaret}
      />
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className={cn("text-xs", problem ? "text-danger-11" : "tabular-nums text-text-subtle")}>
          {problem ?? `${value.length.toLocaleString()} of ${TEXT_MAX.toLocaleString()}`}
        </span>
        {!readOnly && <InsertTextField fields={fields} onOpen={keepCaret} onPick={putField} onPicked={backToText} />}
      </div>
    </div>
  );
}

/** The fields a text can use, by what they are about, as the greeting form lists a greeting's.
 *  A pick writes the field in where the caret was and hands the focus back to the words. */
function InsertTextField({
  fields, onOpen, onPick, onPicked,
}: {
  fields: readonly GreetingField[] | undefined;
  onOpen: () => void;
  onPick: (field: GreetingField) => void;
  onPicked: () => void;
}) {
  const groups = [...new Set((fields ?? []).map((f) => f.group))];
  const picked = React.useRef(false);
  return (
    <DropdownMenu
      onOpenChange={(open) => {
        if (!open) return;
        picked.current = false;
        onOpen();
      }}
    >
      <DropdownMenuTrigger asChild>
        <Button type="button" variant="outline" size="sm" disabled={!fields || fields.length === 0}>
          <Braces />
          Insert field
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="max-h-80 w-72 overflow-y-auto"
        onCloseAutoFocus={(e) => {
          if (!picked.current) return;
          e.preventDefault();
          onPicked();
        }}
      >
        {groups.map((group) => (
          <React.Fragment key={group}>
            <DropdownMenuLabel>{group}</DropdownMenuLabel>
            {(fields ?? [])
              .filter((f) => f.group === group)
              .map((f) => (
                <DropdownMenuItem
                  key={f.name}
                  onSelect={() => {
                    picked.current = true;
                    onPick(f);
                  }}
                  className="flex-col items-start gap-0"
                >
                  <span>{f.label}</span>
                  <span className="text-xs text-text-muted">
                    {f.hint}
                    {f.example ? `. Reads as “${f.example}”` : ""}
                  </span>
                </DropdownMenuItem>
              ))}
          </React.Fragment>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid gap-x-4 gap-y-1.5 px-3 py-2.5 @md:grid-cols-[9.5rem_minmax(0,1fr)]">
      <p className="pt-1 text-[11px] font-semibold uppercase tracking-wide text-text-subtle">{label}</p>
      <div className="flex min-w-0 flex-col items-start gap-1.5">{children}</div>
    </div>
  );
}

function Help({ children }: { children: React.ReactNode }) {
  return <p className="text-xs text-text-muted">{children}</p>;
}

/** Said beside "Dialer leaves a voicemail" (ADR-109 in platform): the message is timed, not
 *  played at the beep, and where the dialer leaves it at all. It goes when the switch reports the
 *  beep, and the wait with it. */
function TimedForNow() {
  return (
    <p className="inline-flex items-start gap-1.5 rounded-md bg-warning-2 px-2 py-1 text-[11px] leading-4 text-warning-11">
      <AlertTriangle className="mt-px size-3 shrink-0" aria-hidden />
      <span>
        <span className="font-semibold">Timed for now.</span> The dialer does not hear the beep yet, so it waits the
        seconds set here: a greeting longer than that cuts off the start of the message. It needs voicemail detection
        on the campaign, with Passive off. On any other campaign the voicemail goes to the rep, who leaves the
        message.
      </span>
    </p>
  );
}

/** How long the dialer waits before it plays a message it leaves by itself. */
function AfterSelect({
  label, value, disabled, onChange,
}: {
  label: string;
  value: number;
  disabled: boolean;
  onChange: (seconds: number) => void;
}) {
  return (
    <Select value={String(value)} onValueChange={(v) => onChange(Number(v))} disabled={disabled}>
      <SelectTrigger aria-label={label} className="h-7 w-auto min-w-[8.5rem] text-xs">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {afterOptions(value).map((n) => (
          <SelectItem key={n} value={String(n)} className="text-xs">
            {afterWords(n)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/** Which voicemail to leave: the message for the lead's stage, or one of the library's by name. */
function MessageSelect({
  label, value, messages, disabled, onChange,
}: {
  label: string;
  value: string;
  messages: VoicemailMessage[];
  disabled: boolean;
  onChange: (slug: string) => void;
}) {
  const known = messages.some((m) => m.slug === value);
  return (
    <Select value={value || BY_STAGE} onValueChange={(v) => onChange(v === BY_STAGE ? "" : v)} disabled={disabled}>
      <SelectTrigger aria-label={label} className="h-7 w-auto min-w-[11rem] flex-1 text-xs">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={BY_STAGE} className="text-xs" description="From the voicemail library, by the lead's stage and language.">
          The message for the lead&apos;s stage
        </SelectItem>
        {value && !known && (
          <SelectItem value={value} className="text-xs" description="Not in the library: the stage's message is left instead.">
            {value}
          </SelectItem>
        )}
        {messages.map((m) => (
          <SelectItem key={m.slug} value={m.slug} className="text-xs">
            {m.name}
            {m.language ? ` (${m.language})` : ""}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// --- the rules every phase shares --------------------------------------------------------------------

function Rules({
  values, set, readOnly, problems,
}: {
  values: CadenceFormValues;
  set: Set;
  readOnly: boolean;
  problems: Record<string, string>;
}) {
  const keys = [
    "technical_retry_minutes", "max_retries", "invalid_stops_after", "not_worked_minutes",
    "callback_default_minutes", "callback_kept_for_minutes", "callback_window_minutes", "priority_callback", "priority_about_to_sign",
    "priority_mid_cycle", "priority_initial_fresh",
  ];
  const troubled = keys.some((k) => problems[k]);
  const [open, setOpen] = React.useState(false);
  const shown = open || troubled;
  const put = <K extends keyof CadenceFormValues>(key: K, value: CadenceFormValues[K], coalesce?: string) =>
    set((v) => ({ ...v, [key]: value }), coalesce);
  const band = (key: "priority_callback" | "priority_about_to_sign" | "priority_mid_cycle" | "priority_initial_fresh", label: string) => (
    <Token
      label={label}
      text={String(values[key])}
      readOnly={readOnly}
      invalid={problems[key]}
      title={label}
      content={() => (
        <PriorityPicker
          band={values[key]}
          selfLabel={label}
          others={[
            { label: "A promised callback", value: values.priority_callback, key: "priority_callback" },
            { label: "A lead near signing", value: values.priority_about_to_sign, key: "priority_about_to_sign" },
            { label: "Second call of a double dial", value: values.priority_mid_cycle, key: "priority_mid_cycle" },
            { label: "A lead never reached", value: values.priority_initial_fresh, key: "priority_initial_fresh" },
            ...values.phases.map((p) => ({ label: `Phase “${p.name}”`, value: p.priority_band, key: p.name })),
          ].filter((r) => r.key !== key)}
          onChange={(n) => put(key, n, key)}
        />
      )}
    >
      <span className="tabular-nums">{values[key]}</span>
    </Token>
  );
  const counts = (key: "abandoned_counts" | "manual_dial_counts" | "inbound_counts", label: string) => (
    <Token
      label={label}
      readOnly={readOnly}
      title={label}
      content={(close) => (
        <OptionPicker value={values[key] ? "yes" : "no"} options={COUNTS} onChange={(c) => put(key, c === "yes")} close={close} />
      )}
    >
      {values[key] ? "counts" : "does not count"}
    </Token>
  );

  return (
    <section className="mt-6 overflow-hidden rounded-xl border border-border bg-surface" data-tour="cadence-rules">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={shown}
        className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-surface-hover"
      >
        <span className="grid size-7 shrink-0 place-items-center rounded-lg bg-gray-3 text-text-muted" aria-hidden>
          <Settings2 className="size-3.5" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-medium text-text">More rules</span>
          <span className="block truncate text-xs text-text-muted">
            For every phase: our failures retried {values.max_retries}× after {waitWords(values.technical_retry_minutes)} · a
            hang-up while connecting retried after {waitWords(values.not_worked_minutes)} · callbacks first ({values.priority_callback})
          </span>
        </span>
        <ChevronDown className={cn("size-4 shrink-0 text-text-subtle transition-transform", shown && "rotate-180")} aria-hidden />
      </button>
      {shown && (
        <div className="grid border-t border-border text-sm leading-7 text-text-muted @lg:grid-cols-2">
          <Rule title="A call fails on our side">
            A trunk or switch failure is tried again{" "}
            <Token
              label="Retry a failure after"
              readOnly={readOnly}
              invalid={problems.technical_retry_minutes}
              title="Try a failed call again after"
              content={(close) => (
                <WaitPicker
                  value={values.technical_retry_minutes}
                  presets={WAIT_PRESETS.technical}
                  min={CADENCE_BOUNDS.technical_retry_minutes[0]}
                  max={CADENCE_BOUNDS.technical_retry_minutes[1]}
                  onChange={(m) => put("technical_retry_minutes", m)}
                  close={close}
                />
              )}
            >
              {waitWords(values.technical_retry_minutes)}
            </Token>{" "}
            later, up to{" "}
            <Token
              label="Retries"
              readOnly={readOnly}
              invalid={problems.max_retries}
              title="Tries after a failure"
              content={(close) => (
                <CountPicker
                  value={values.max_retries}
                  min={CADENCE_BOUNDS.max_retries[0]}
                  max={CADENCE_BOUNDS.max_retries[1]}
                  presets={[0, 1, 2, 3, 5]}
                  unit="times"
                  onChange={(n) => put("max_retries", n ?? 0, "retries")}
                  close={close}
                />
              )}
            >
              {`${values.max_retries} time${values.max_retries === 1 ? "" : "s"}`}
            </Token>
            , and it does not count. A number the carrier says is not in service is dropped after{" "}
            <Token
              label="Invalid number, stop after"
              readOnly={readOnly}
              invalid={problems.invalid_stops_after}
              title="Not in service, in a row"
              content={(close) => (
                <CountPicker
                  value={values.invalid_stops_after}
                  min={CADENCE_BOUNDS.invalid_stops_after[0]}
                  max={CADENCE_BOUNDS.invalid_stops_after[1]}
                  presets={[1, 2, 3]}
                  unit="in a row"
                  onChange={(n) => put("invalid_stops_after", n ?? 1, "invalid")}
                  close={close}
                />
              )}
            >
              {`${values.invalid_stops_after} tr${values.invalid_stops_after === 1 ? "y" : "ies"}`}
            </Token>{" "}
            in a row.
          </Rule>
          <Rule title="The lead hangs up while connecting">
            They are called again{" "}
            <Token
              label="Hung up while connecting, again after"
              readOnly={readOnly}
              invalid={problems.not_worked_minutes}
              title="Called again after hanging up while connecting"
              content={(close) => (
                <WaitPicker
                  value={values.not_worked_minutes}
                  presets={WAIT_PRESETS.notWorked}
                  min={CADENCE_BOUNDS.not_worked_minutes[0]}
                  max={CADENCE_BOUNDS.not_worked_minutes[1]}
                  onChange={(m) => put("not_worked_minutes", m)}
                  close={close}
                />
              )}
            >
              {waitWords(values.not_worked_minutes)}
            </Token>{" "}
            later, and it does not count.
          </Rule>
          <Rule title="A callback with no time">
            A callback promised with no time comes back{" "}
            <Token
              label="A callback with no time"
              readOnly={readOnly}
              invalid={problems.callback_default_minutes}
              title="A callback with no time"
              hint="Kept for the rep who promised it."
              content={(close) => (
                <WaitPicker
                  value={values.callback_default_minutes}
                  presets={WAIT_PRESETS.callback}
                  min={CADENCE_BOUNDS.wait_minutes[0]}
                  max={CADENCE_BOUNDS.wait_minutes[1]}
                  onChange={(m) => put("callback_default_minutes", m)}
                  close={close}
                />
              )}
            >
              {waitWords(values.callback_default_minutes)}
            </Token>{" "}
            later, for the rep who promised it.
          </Rule>
          <Rule title="Who makes a callback">
            A callback waits for the rep who promised it for{" "}
            <Token
              label="How long a callback waits for its rep"
              readOnly={readOnly}
              invalid={problems.callback_kept_for_minutes}
              title="How long a callback waits for its rep"
              hint="Counted from the callback's time. A dropped call waits the same."
              content={(close) => (
                <WaitPicker
                  value={values.callback_kept_for_minutes}
                  presets={WAIT_PRESETS.callbackKept}
                  min={CADENCE_BOUNDS.callback_kept_for_minutes[0]}
                  max={CADENCE_BOUNDS.callback_kept_for_minutes[1]}
                  onChange={(m) => put("callback_kept_for_minutes", m)}
                  close={close}
                />
              )}
            >
              {waitWords(values.callback_kept_for_minutes)}
            </Token>
            , then any rep on the campaign may make it.
          </Rule>
          <Rule title="How long a callback is worked">
            {values.callback_window_minutes === null ? "After a callback's time, " : "Keep trying for "}
            <Token
              label="How long a callback is worked"
              readOnly={readOnly}
              invalid={problems.callback_window_minutes}
              title="How long a callback is worked"
              hint="With the phase's own calls, redials and waits, until the window ends. A call that would start past it is not made, and the callback is then missed. A backup time must be a whole window after the time before it."
              content={(close) => (
                <>
                  <OptionPicker
                    value={values.callback_window_minutes === null ? "none" : "window"}
                    options={[{ value: "none", label: "Just its call and redials", description: "Missed the moment its last redial reaches nobody." }]}
                    onChange={() => put("callback_window_minutes", null)}
                    close={close}
                  />
                  <div className="mt-2 border-t border-border pt-2">
                    <p className="mb-1.5 text-[11px] font-medium uppercase tracking-wide text-text-subtle">Keep trying for</p>
                    <WaitPicker
                      value={values.callback_window_minutes ?? 60}
                      presets={WAIT_PRESETS.callbackWindow}
                      min={CADENCE_BOUNDS.callback_window_minutes[0]}
                      max={CADENCE_BOUNDS.callback_window_minutes[1]}
                      onChange={(m) => put("callback_window_minutes", m)}
                      close={close}
                    />
                  </div>
                </>
              )}
            >
              {values.callback_window_minutes === null ? "just its call and redials" : waitWords(values.callback_window_minutes)}
            </Token>
            {values.callback_window_minutes === null ? "." : " after a callback's time, with the phase's calls and redials."}
          </Rule>
          <Rule title="What counts as an attempt" wide>
            An abandoned call (the lead answered, no rep was free) {counts("abandoned_counts", "An abandoned call")}. A rep&apos;s
            hand dial through the campaign {counts("manual_dial_counts", "A rep's hand dial")}. A call the lead makes to us{" "}
            {counts("inbound_counts", "A call the lead makes")}. A redial always counts.
            {hasTexts(values) ? " A text never does." : ""}
          </Rule>
          <Rule title="Who is called first when reps are short" wide>
            A promised callback {band("priority_callback", "A promised callback")}, a lead near signing{" "}
            {band("priority_about_to_sign", "A lead near signing")}, the second call of a double dial{" "}
            {band("priority_mid_cycle", "Second call of a double dial")}, a lead never reached{" "}
            {band("priority_initial_fresh", "A lead never reached")}. Everyone else takes their phase&apos;s priority. Higher
            goes first; the calling hours, a hold and the daily cap always come before priority.
          </Rule>
        </div>
      )}
    </section>
  );
}

function Rule({ title, wide = false, children }: { title: string; wide?: boolean; children: React.ReactNode }) {
  return (
    <div className={cn("border-b border-border px-4 py-3 last:border-b-0", wide ? "@lg:col-span-2" : "@lg:odd:border-r")}>
      <p className="text-xs font-semibold uppercase tracking-wide text-text-subtle">{title}</p>
      <p>{children}</p>
    </div>
  );
}

// --- the flow's pieces -------------------------------------------------------------------------------

function Card({
  icon: Icon, tone, title, tour, children,
}: {
  icon: React.ComponentType<{ className?: string }>;
  tone: "start" | "end";
  title: string;
  /** The card's name in the page's tour. */
  tour: string;
  children: React.ReactNode;
}) {
  return (
    <article className="flex gap-3 rounded-xl border border-border bg-surface px-4 py-3 shadow-xs" data-tour={tour}>
      <span
        className={cn(
          "grid size-8 shrink-0 place-items-center rounded-lg",
          tone === "start" ? "bg-accent-9 text-white dark:text-gray-1" : "bg-gray-12 text-gray-1 dark:bg-gray-11",
        )}
        aria-hidden
      >
        <Icon className="size-4" />
      </span>
      <div className="min-w-0 flex-1 space-y-1 text-sm leading-7 text-text-muted">
        <h3 className="text-sm font-semibold leading-8 text-text">{title}</h3>
        {children}
      </div>
    </article>
  );
}

function Connector({ label, children }: { label?: string; children?: React.ReactNode }) {
  return (
    <div className="flex flex-col items-center">
      <span className="h-3 w-px bg-border-strong" aria-hidden />
      {label && (
        <span className="rounded-full border border-border bg-surface-sunken px-2 py-0.5 text-[11px] font-medium text-text-muted">
          {label}
        </span>
      )}
      {children}
      <span className="h-3 w-px bg-border-strong" aria-hidden />
    </div>
  );
}

/** The dotted line between two calls of a cycle. */
function Rail() {
  return <span className="absolute bottom-0 left-3 top-0 w-px border-l border-dashed border-border-strong" aria-hidden />;
}
